import { Alert, Card, CardContent, CardHeader, Grid, Skeleton } from '@mui/material';
import { AxiosError } from 'axios';
import { useMemo, useState } from 'react';
import { HashUnit, convertHash, formatThoundsandNumber } from '../../../../abelcommon';
import AnalyticsRoundedIcon from '@mui/icons-material/AnalyticsRounded';
import './globalNetworkStyle.css';
import { DifficultyTrendDialog } from './DifficultyTrendDialog';
import { AverageBlockTimeDialog } from './AverageBlockTimeDialog';
import { NetworkSummaryInfo } from '../../../../api/abel';

export const GlobalNetwork = ({
  globalNetworkInfo,
  globalNetworkInfoLoading,
  globalNetworkInfoError,
}: {
  globalNetworkInfo: NetworkSummaryInfo;
  globalNetworkInfoLoading: boolean;
  globalNetworkInfoError: AxiosError;
}) => {
  const [open, setOpen] = useState(false);
  const [modalContent, setModalContent] = useState<React.ReactNode | null>(null);

  const handleClickOpen = (content: React.ReactNode) => {
    setModalContent(content);
    setOpen(true);
  };

  const handleClose = () => {
    setModalContent(null);
    setOpen(false);
  };

  const summaryInfo = useMemo(() => {
    const propsPlaceholder = [
      { key: 'Latest Block', description: '', value: '', icon: '' },
      { key: 'Latest Difficulty', description: '', value: '', icon: '' },
      { key: 'Avg Network Hash Rate', description: '', value: '', icon: '' },
      { key: 'Avg Block Time (1000 blocks)', description: '', value: '', icon: '' },
    ];

    return !globalNetworkInfo
      ? propsPlaceholder
      : [
          {
            key: 'Latest Block',
            description: '',
            value: formatThoundsandNumber(globalNetworkInfo.latestBlock.height),
            icon: '',
          },
          {
            key: 'Latest Difficulty',
            description: '',
            value: formatThoundsandNumber(globalNetworkInfo.latestBlock.difficulty),
            icon: <AnalyticsRoundedIcon style={{ fontSize: '30px' }} />,
          },
          {
            key: 'Avg Network Hash Rate',
            description: '',
            value: `${formatThoundsandNumber(convertHash(globalNetworkInfo.networkHashRateLast1K, HashUnit.GH))} GH/s`,
            icon: '',
          },
          {
            key: 'Avg Block Time (1000 blocks)',
            description: '',
            value: `${formatThoundsandNumber(globalNetworkInfo.avgBlockTimeLast1k)} secs`,
            icon: <AnalyticsRoundedIcon style={{ fontSize: '30px' }} />,
          },
        ];
  }, [globalNetworkInfo]);

  return (
    <Card>
      <CardHeader title="Global Network" />
      <CardContent sx={{ overflow: 'auto' }}>
        {globalNetworkInfoError ? (
          <Alert severity="error" sx={{ mb: 1 }}>
            {globalNetworkInfoError?.message}
          </Alert>
        ) : (
          <Grid container spacing={1}>
            {summaryInfo.map(({ key, value, icon, description }) => (
              <Grid item xs={6} sm={4} md={4} lg={3} xl={3} key={key}>
                <InfoBox
                  icon={icon}
                  title={value}
                  description={description}
                  content={key}
                  isLoading={globalNetworkInfoLoading}
                  onClick={() => handleClickOpen(key)}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </CardContent>

      {open && modalContent === 'Latest Difficulty' && (
        <DifficultyTrendDialog open={open && modalContent === 'Latest Difficulty'} onClose={handleClose} />
      )}

      {open && modalContent === 'Avg Block Time (1000 blocks)' && (
        <AverageBlockTimeDialog open={open && modalContent === 'Avg Block Time (1000 blocks)'} onClose={handleClose} />
      )}
    </Card>
  );
};

interface InfoBoxProps {
  title: string;
  content: string;
  isLoading: boolean;
  icon: React.ReactNode;
  description: string;
  onClick?: () => void;
}

const InfoBox = ({ title, description, content, isLoading, icon, onClick }: InfoBoxProps) => {
  return isLoading ? (
    <Skeleton variant="rectangular" sx={{ minHeight: '82px', borderRadius: 1 }} />
  ) : (
    <div className="global-item-network">
      <span className="content-global">
        {content}
        <span style={{ fontSize: '12px' }}>{description}</span>
      </span>
      <div className="title-icon-global">
        <span>{title}</span>
        <span className="icon" onClick={onClick}>
          {icon}
        </span>
      </div>
    </div>
  );
};
